import React from 'react';
import { cn } from '../../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

export interface MetadataBadgeProps {
  /** Icon element to display */
  icon: React.ReactNode;
  /** Value to display (number or string) */
  value: number | string;
  /** Tooltip text */
  tooltip?: string;
  /** Click handler - if provided, badge becomes interactive with hover state */
  onClick?: (e: React.MouseEvent) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Small badge component displaying an icon + value, optionally clickable.
 *
 * When onClick is provided:
 * - Shows hover background
 * - Cursor becomes pointer
 * - Useful for navigation badges (notes count, files count, etc.)
 *
 * When onClick is not provided:
 * - Static display
 * - Useful for read-only metadata (days in stage, etc.)
 */
export function MetadataBadge({
  icon,
  value,
  tooltip,
  onClick,
  className,
}: MetadataBadgeProps) {
  const isClickable = !!onClick;

  const content = (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? (e) => {
        e.stopPropagation();
        onClick(e);
      } : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onClick(e as unknown as React.MouseEvent);
        }
      } : undefined}
      className={cn(
        'inline-flex items-center gap-1 text-muted-foreground rounded px-1 py-0.5',
        isClickable && 'cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors',
        className
      )}
    >
      {icon}
      <span className="text-xs">{value}</span>
    </div>
  );

  if (!tooltip) return content;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
